import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MapPinLine, Phone, HouseLine, Plus, CaretDown, CaretUp, CheckCircle, Truck,
  Camera, SealCheck, LockKey, PaperPlaneRight, Trash, Package, ClockCountdown,
  User, MapPin, DeviceMobile, PencilSimple,
} from "@phosphor-icons/react";
import { api, formatApiError, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from "../components/ui/dialog";
import { VoiceTypingButton } from "../components/VoiceTypingButton";
import { VoiceRecorderButton, VoicePlayer } from "../components/VoiceRecorder";

export const STAGES = [
  { key: "pending", label: "Inspection Pending", color: "#64748B" },
  { key: "submitted", label: "Approval Pending", color: "#F59E0B", pulse: true },
  { key: "approved", label: "Dispatch Pending", color: "#10B981" },
  { key: "dispatched", label: "Material Dispatched", color: "#6366F1" },
  { key: "install_submitted", label: "Install Approval Pending", color: "#06B6D4", pulse: true },
  { key: "completed", label: "Completed", color: "#16A34A" },
];
const stageIdx = (s) => STAGES.findIndex((x) => x.key === s);

const Stepper = ({ status }) => {
  const idx = stageIdx(status);
  return (
    <div className="flex items-center gap-0.5 w-full" data-testid="stage-stepper">
      {STAGES.map((s, i) => (
        <div key={s.key} className="flex-1 h-1.5 rounded-full transition-colors"
          style={{ backgroundColor: i <= idx ? s.color : "var(--border, #E2E8F0)" }} />
      ))}
    </div>
  );
};

const StageBadge = ({ status }) => {
  const s = STAGES[stageIdx(status)] || STAGES[0];
  return (
    <span data-testid={`stage-badge-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${s.pulse ? "animate-pulse" : ""}`}
      style={{ backgroundColor: `${s.color}1A`, color: s.color, border: `1px solid ${s.color}55` }}>
      {s.pulse && <ClockCountdown size={11} weight="bold" />}{s.label}
    </span>
  );
};

const FiPhoto = ({ itemId, photo }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/field-items/${itemId}/photos/${photo.id}`, { responseType: "blob" })
      .then((r) => alive && setUrl(URL.createObjectURL(r.data))).catch(() => {});
    return () => { alive = false; };
  }, [photo.id]);
  return (
    <button data-testid={`fi-photo-${photo.id}`} onClick={() => url && window.open(url, "_blank")}
      className="aspect-square rounded-lg overflow-hidden border border-border bg-muted relative">
      {url ? <img src={url} alt="site" className="w-full h-full object-cover" /> : <span className="absolute inset-0 animate-pulse bg-muted" />}
      <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[8px] px-1 py-0.5 truncate">{photo.kind} · {photo.by_name}</span>
    </button>
  );
};

const PhotoUploader = ({ itemId, kind, onDone, label }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/field-items/${itemId}/photos`, fd);
      toast.success("Photo uploaded");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button data-testid={`photo-upload-${kind}`} type="button" variant="outline" size="sm" disabled={busy}
        className="h-9 px-3 rounded-full text-xs active:scale-95" onClick={() => ref.current?.click()}>
        <Camera size={15} className="mr-1" /> {busy ? "Uploading..." : label || "Photo"}
      </Button>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
    </>
  );
};

const MAT_EXCLUDE = ["Solar Panel", "Inverter", "Battery", "Rafter", "Purlin", "Leg Pillar", "पाए / Leg Pillar"];

// "Add Material" popup — all products listed in one window, fill quantity + add extra items, save
const MaterialPicker = ({ catalog, values, setValues }) => {
  const items = catalog.filter((c) => !MAT_EXCLUDE.includes(c.name));
  const [open, setOpen] = useState(false);
  const [cName, setCName] = useState("");
  const [cQty, setCQty] = useState("");

  const get = (id) => values[id] || { qty: "", spec: "" };
  const setV = (c, patch) => setValues({ ...values, [c.id]: { ...get(c.id), name: c.name, unit: c.unit, ...patch } });
  const filled = Object.values(values).filter((v) => Number(v.qty) > 0);

  const addCustom = () => {
    if (!cName.trim() || !(Number(cQty) > 0)) return;
    const key = `custom-${Date.now()}`;
    setValues({ ...values, [key]: { name: cName.trim(), qty: cQty, unit: "pcs", custom: true } });
    setCName(""); setCQty("");
  };
  const removeKey = (id) => { const n = { ...values }; delete n[id]; setValues(n); };
  const customEntries = Object.entries(values).filter(([, v]) => v.custom);

  return (
    <div className="space-y-2" data-testid="material-picker">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Package size={14} /> Materials</span>
        <Button type="button" size="sm" variant="outline" data-testid="add-material-btn"
          className="h-9 rounded-full px-4 text-xs font-semibold active:scale-95" onClick={() => setOpen(true)}>
          <Package size={15} className="mr-1" /> Fill Materials{filled.length ? ` (${filled.length})` : ""}
        </Button>
      </div>

      {filled.length === 0 ? (
        <p className="text-xs text-muted-foreground border border-dashed border-border rounded-xl px-3 py-3 text-center">No materials filled yet</p>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden bg-card divide-y divide-border" data-testid="material-added-list">
          {filled.map((v, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1 font-medium truncate">{v.name}{v.spec ? <span className="text-muted-foreground"> · {v.spec}</span> : ""}</span>
              <span className="font-mono font-semibold text-sm">{v.qty} {v.unit || ""}</span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="add-material-dialog">
          <DialogHeader className="p-5 pb-3 border-b border-border">
            <DialogTitle>Fill Materials</DialogTitle>
            <DialogDescription>Enter the quantity for each required item. Add any extra item at the bottom.</DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-y-auto">
            <div className="divide-y divide-border">
              {items.map((c) => {
                const v = get(c.id);
                const active = Number(v.qty) > 0;
                return (
                  <div key={c.id} className={`flex items-center gap-2 px-4 py-2.5 ${active ? "bg-emerald-50/70" : ""}`}>
                    <div className="min-w-0 flex-1 text-[13px] font-medium">{c.name}</div>
                    <input data-testid={`material-qty-${c.id}`} type="number" min="0" inputMode="numeric" value={v.qty}
                      onChange={(e) => setV(c, { qty: e.target.value })} placeholder="Qty"
                      className="w-16 h-9 text-center text-sm font-mono font-semibold rounded-lg border border-border bg-background shrink-0" />
                    <span className="text-[9px] text-muted-foreground font-mono w-7 text-center shrink-0">{c.unit}</span>
                  </div>
                );
              })}
            </div>

            <div className="px-4 py-3 bg-muted/30 border-t border-border space-y-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Additional Items</div>
              {customEntries.map(([id, v]) => (
                <div key={id} className="flex items-center gap-2 text-[13px] bg-card border border-border rounded-lg px-3 py-1.5">
                  <span className="min-w-0 flex-1 truncate">{v.name}</span>
                  <span className="font-mono font-semibold">{v.qty} {v.unit}</span>
                  <button type="button" data-testid={`material-remove-${id}`} onClick={() => removeKey(id)} className="text-red-500 p-1 rounded hover:bg-red-50"><Trash size={13} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input data-testid="custom-item-name" value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Item name" className="h-9 text-sm flex-1" />
                <Input data-testid="custom-item-qty" type="number" min="0" inputMode="numeric" value={cQty} onChange={(e) => setCQty(e.target.value)} placeholder="Qty" className="h-9 text-sm w-20 text-center" />
                <Button type="button" data-testid="custom-item-add" size="sm" variant="outline" className="h-9 rounded-full px-3 text-xs shrink-0" disabled={!cName.trim() || !(Number(cQty) > 0)} onClick={addCustom}>
                  <Plus size={14} /> Add
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="p-4 border-t border-border">
            <DialogClose asChild>
              <Button type="button" className="w-full rounded-full font-semibold h-11" data-testid="material-dialog-done">
                Save Materials{filled.length ? ` (${filled.length})` : ""}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SizeRows = ({ label, rows, setRows, testPrefix }) => (
  <div className="space-y-1.5">
    <div className="text-[11px] font-bold text-muted-foreground">{label}</div>
    {rows.map((r, i) => (
      <div key={i} className="flex items-center gap-2">
        <input data-testid={`${testPrefix}-feet-${i}`} type="number" min="0" inputMode="numeric" value={r.feet}
          onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, feet: e.target.value } : x)))}
          placeholder="Feet" className="w-24 h-10 text-center text-sm font-mono rounded-lg border border-border bg-background" />
        <span className="text-xs text-muted-foreground">ft ×</span>
        <input data-testid={`${testPrefix}-qty-${i}`} type="number" min="0" inputMode="numeric" value={r.qty}
          onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
          placeholder="Qty" className="w-20 h-10 text-center text-sm font-mono rounded-lg border border-border bg-background" />
        <span className="text-xs text-muted-foreground">nos</span>
        <button type="button" data-testid={`${testPrefix}-remove-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}
          className="ml-auto text-red-500 p-1.5 rounded hover:bg-red-50"><Trash size={14} /></button>
      </div>
    ))}
    <button type="button" data-testid={`${testPrefix}-add-row`} onClick={() => setRows([...rows, { feet: "", qty: "" }])}
      className="text-[11px] font-semibold text-violet-700 inline-flex items-center gap-1 hover:underline"><Plus size={12} weight="bold" /> Add size</button>
  </div>
);

const StructureSection = ({ surface, setSurface, rafters, setRafters, purlins, setPurlins }) => (
  <div className="rounded-xl border border-border bg-card p-3 space-y-3" data-testid="structure-section">
    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Mounting Structure</div>
    <div>
      <div className="text-[11px] font-bold text-muted-foreground mb-1.5">What is the installation surface like?</div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid="surface-flat" onClick={() => setSurface("flat")}
          className={`h-11 rounded-xl border-2 text-sm font-semibold transition-all active:scale-95 ${surface === "flat" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-border bg-card"}`}>
          Flat
        </button>
        <button type="button" data-testid="surface-uneven" onClick={() => setSurface("uneven")}
          className={`h-11 rounded-xl border-2 text-sm font-semibold transition-all active:scale-95 ${surface === "uneven" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-border bg-card"}`}>
          Uneven
        </button>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
      <SizeRows label="Rafter" rows={rafters} setRows={setRafters} testPrefix="rafter" />
      <SizeRows label="Purlin" rows={purlins} setRows={setPurlins} testPrefix="purlin" />
    </div>
  </div>
);

const EQUIP_META = [
  ["panels", "Solar Panel"],
  ["batteries", "Battery"],
  ["inverters", "Inverter"],
];

const EquipmentForm = ({ equip, setEquip }) => (
  <div className="space-y-2.5" data-testid="equipment-form">
    {EQUIP_META.map(([key, label]) => {
      const rows = equip[key];
      const setRows = (r) => setEquip({ ...equip, [key]: r });
      return (
        <div key={key} className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-[70px] shrink-0 text-[11px] font-bold text-muted-foreground">{i === 0 ? label : ""}</div>
              <input data-testid={`eq-${key}-brand-${i}`} value={r.brand} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, brand: e.target.value } : x)))}
                placeholder="Brand" className="flex-1 min-w-0 h-10 text-sm px-2 rounded-lg border border-border bg-white" />
              <input data-testid={`eq-${key}-cap-${i}`} value={r.capacity} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, capacity: e.target.value } : x)))}
                placeholder="Capacity" className="w-[90px] h-10 text-xs px-2 rounded-lg border border-border bg-white shrink-0" />
              <input data-testid={`eq-${key}-qty-${i}`} type="number" min="0" inputMode="numeric" value={r.qty} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                placeholder="Qty" className="w-14 h-10 text-center text-sm font-mono rounded-lg border border-border bg-white shrink-0" />
              {rows.length > 1 && (
                <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-red-500 p-1 rounded hover:bg-red-50 shrink-0"><Trash size={13} /></button>
              )}
            </div>
          ))}
          <button type="button" data-testid={`eq-${key}-add`} onClick={() => setRows([...rows, { brand: "", capacity: "", qty: "" }])}
            className="text-[11px] font-semibold text-violet-700 inline-flex items-center gap-1 hover:underline ml-[76px]"><Plus size={11} weight="bold" /> Add {label}</button>
        </div>
      );
    })}
  </div>
);

// Create inspection popup — customer name, address, mobile, technician
const CreateInspectionDialog = ({ open, setOpen, techs, busy, act }) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [mobile, setMobile] = useState("");
  const [tech, setTech] = useState("");
  const addrRef = useRef(null);
  const reset = () => { setName(""); setAddress(""); setMobile(""); setTech(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogContent className="max-w-md" data-testid="create-inspection-dialog">
        <DialogHeader>
          <DialogTitle>New Inspection</DialogTitle>
          <DialogDescription>Enter customer details and assign a technician.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><User size={13} /> Customer Name</label>
            <Input data-testid="insp-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" className="h-11" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><MapPin size={13} /> Address</label>
            <div className="flex gap-2">
              <Input ref={addrRef} data-testid="insp-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" className="h-11" />
              <VoiceTypingButton inputRef={addrRef} onText={setAddress} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><DeviceMobile size={13} /> Mobile Number</label>
            <Input data-testid="insp-mobile-input" type="tel" inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile number" className="h-11" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1 block">Assign Technician</label>
            <Select value={tech} onValueChange={setTech}>
              <SelectTrigger data-testid="insp-tech-select" className="h-11"><SelectValue placeholder="Select technician" /></SelectTrigger>
              <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
          </DialogClose>
          <Button data-testid="insp-create-btn" className="rounded-full px-5 font-semibold" disabled={!name.trim() || !tech || busy}
            onClick={() => act(async () => {
              await api.post("/field-items", {
                type: "inspection", customer_name: name.trim(),
                customer_address: address.trim() || null, customer_mobile: mobile.trim() || null,
                text: address.trim() || null, assigned_to: tech,
              });
              reset(); setOpen(false);
            }, "Inspection assigned")}>Create Inspection</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Inspections() {
  const { user } = useAuth();
  const isMgmt = user.role === "super_admin" || user.role === "manager";
  const isAdmin = user.role === "super_admin";
  const isWarehouse = user.role === "warehouse";
  const canAssign = (user.permissions || []).includes("insp_create");
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [techs, setTechs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => api.get("/field-items", { params: { type: "inspection" } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/materials-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    if (canAssign) api.get("/users/technicians").then((r) => setTechs(r.data)).catch(() => {});
  }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    STAGES.forEach((s) => { c[s.key] = items.filter((x) => x.status === s.key).length; });
    return c;
  }, [items]);

  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);

  return (
    <div className="max-w-5xl mx-auto space-y-5 stagger-in pb-24" data-testid="inspections-page">
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-brand-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-brand-600/25"><MapPinLine size={24} weight="bold" /></span>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900">Inspections &amp; installs</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Inspection → Approval → Dispatch → Installation</p>
        </div>
        {canAssign && (
          <Button data-testid="insp-create-toggle" aria-label="New inspection" className="h-11 w-11 sm:w-auto rounded-full sm:px-5 font-semibold shadow-md shadow-brand-600/20" onClick={() => setShowCreate(true)}>
            <Plus size={18} weight="bold" /><span className="hidden sm:inline">New inspection</span>
          </Button>
        )}
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        <button data-testid="insp-filter-all" onClick={() => setFilter("all")}
          className={`shrink-0 h-9 px-3.5 rounded-full border text-xs font-semibold transition-all active:scale-95 ${filter === "all" ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-muted"}`}>
          All <span className="font-mono text-[10px] opacity-70">{counts.all}</span>
        </button>
        {STAGES.map((s) => (
          <button key={s.key} data-testid={`insp-filter-${s.key}`} onClick={() => setFilter(s.key)}
            className={`shrink-0 h-9 px-3.5 rounded-full border text-xs font-semibold transition-all active:scale-95 ${filter === s.key ? "text-white" : "bg-card border-border hover:bg-muted"}`}
            style={filter === s.key ? { backgroundColor: s.color, borderColor: s.color } : {}}>
            {s.label} <span className="font-mono text-[10px] opacity-70">{counts[s.key]}</span>
          </button>
        ))}
      </div>

      {canAssign && <CreateInspectionDialog open={showCreate} setOpen={setShowCreate} techs={techs} busy={busy} act={act} />}

      {filtered.length === 0 && (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-muted-foreground" data-testid="insp-empty">No inspections found</div>
      )}

      <div className="space-y-3">
        {filtered.map((x) => (
          <InspectionCard key={x.id} x={x} user={user} isMgmt={isMgmt} isAdmin={isAdmin} isWarehouse={isWarehouse}
            catalog={catalog} busy={busy} act={act} load={load}
            expanded={expanded === x.id} onToggle={() => setExpanded(expanded === x.id ? null : x.id)} />
        ))}
      </div>
    </div>
  );
}

const DeliveryRow = ({ x, ev, can, act, busy }) => {
  const canMark = can("dispatch_material") || can("insp_site_delivery");
  if (!ev.id || (ev.delivered === undefined && !canMark)) return null;
  return (
    <div className="flex items-center justify-between gap-2 pt-1" data-testid={`delivery-row-${ev.id}`}>
      <span className={`font-semibold ${ev.delivered ? "text-emerald-700" : "text-amber-700"}`}>
        {ev.delivered ? `Reached site · ${new Date(ev.delivered_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Not yet confirmed at site"}
      </span>
      {canMark && (
        <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]" disabled={busy} data-testid={`delivery-toggle-${ev.id}`}
          onClick={() => act(() => api.post(`/field-items/${x.id}/dispatches/${ev.id}/delivery`, { delivered: !ev.delivered }), ev.delivered ? "Marked as not delivered" : "Marked as reached site")}>
          {ev.delivered ? "Undo" : "Mark reached"}
        </Button>
      )}
    </div>
  );
};

const InspectionCard = ({ x, user, isMgmt, isAdmin, isWarehouse, catalog, busy, act, expanded, onToggle }) => {
  const isAssignee = x.assigned_to === user.id;
  const address = x.customer_address || (x.customer_name ? x.text : "");
  return (
    <div data-testid={`insp-card-${x.id}`} className={`border rounded-2xl bg-card overflow-hidden shadow-sm ${["submitted", "install_submitted"].includes(x.status) ? "border-amber-400 ring-1 ring-amber-300/60" : "border-border"}`}>
      <button type="button" onClick={onToggle} className="w-full text-left px-4 pt-3.5 pb-3 space-y-2 hover:bg-muted/40 transition-colors">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-[15px] leading-snug truncate flex items-center gap-1.5"><User size={15} className="text-violet-600 shrink-0" /> {x.customer_name || x.text}</div>
            {address && <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5 truncate"><MapPin size={12} className="shrink-0" /> {address}</div>}
            {x.customer_mobile && <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5"><DeviceMobile size={12} className="shrink-0" /> {x.customer_mobile}</div>}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <StageBadge status={x.status} />
            {x.mode && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                {x.mode === "phone_call" ? <Phone size={10} /> : <HouseLine size={10} />}{x.mode === "phone_call" ? "Phone Call" : "Site Visit"}
              </span>
            )}
          </div>
        </div>
        <Stepper status={x.status} />
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{x.assigned_to_name} · {formatDateTime(x.created_at)}</span>
          <span className="flex items-center gap-1">{expanded ? "Close" : "Details"} {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}</span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          <InspectionDetail x={x} user={user} isMgmt={isMgmt} isAdmin={isAdmin} isWarehouse={isWarehouse}
            isAssignee={isAssignee} catalog={catalog} busy={busy} act={act} />
          {(isAdmin || x.created_by === user.id) && (
            <button data-testid={`insp-delete-${x.id}`} onClick={() => window.confirm("Delete this inspection?") && act(() => api.delete(`/field-items/${x.id}`), "Deleted")}
              className="text-red-500 text-[11px] inline-flex items-center gap-1 hover:underline"><Trash size={12} /> Delete inspection</button>
          )}
        </div>
      )}
    </div>
  );
};

const InspectionDetail = ({ x, user, isMgmt, isAdmin, isWarehouse, isAssignee, catalog, busy, act }) => {
  const [mode, setMode] = useState(x.mode || "");
  const [values, setValues] = useState(() => {
    const v = {};
    (x.material_items || []).forEach((m) => {
      const c = catalog.find((c) => c.name === m.name);
      if (c) v[c.id] = { name: m.name, qty: String(m.qty), spec: m.spec || "", unit: m.unit };
    });
    return v;
  });
  const [surface, setSurface] = useState(x.surface_type || "");
  const [rafters, setRafters] = useState(x.structure?.rafters?.length ? x.structure.rafters : [{ feet: "", qty: "" }]);
  const [purlins, setPurlins] = useState(x.structure?.purlins?.length ? x.structure.purlins : [{ feet: "", qty: "" }]);
  const [equip, setEquip] = useState({
    panels: x.equipment?.panels?.length ? x.equipment.panels : [{ brand: "", capacity: "", qty: "" }],
    inverters: x.equipment?.inverters?.length ? x.equipment.inverters : [{ brand: "", capacity: "", qty: "" }],
    batteries: x.equipment?.batteries?.length ? x.equipment.batteries : [{ brand: "", capacity: "", qty: "" }],
  });
  const [note, setNote] = useState(x.note || "");
  const [pvtNote, setPvtNote] = useState(x.private_note || "");
  const [installNote, setInstallNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [matOpen, setMatOpen] = useState(false);
  const noteRef = useRef(null);
  const pvtRef = useRef(null);
  const can = (k) => (user.permissions || []).includes(k);
  const canFill = (x.status === "pending" && (isAssignee || isMgmt)) || (x.status === "submitted" && isAdmin);

  const submit = () => act(async () => {
    const material_items = Object.values(values).filter((v) => Number(v.qty) > 0)
      .map((v) => ({ name: v.name, qty: Number(v.qty), spec: v.spec || null, unit: v.unit || null }));
    const structure = {
      rafters: rafters.filter((r) => Number(r.feet) > 0 && Number(r.qty) > 0).map((r) => ({ feet: Number(r.feet), qty: Number(r.qty) })),
      purlins: purlins.filter((r) => Number(r.feet) > 0 && Number(r.qty) > 0).map((r) => ({ feet: Number(r.feet), qty: Number(r.qty) })),
    };
    await api.post(`/field-items/${x.id}/inspection-submit`,
      { mode, material_items, surface_type: surface || null, structure, note: note || null, private_note: pvtNote || null });
    if (isMgmt && can("admin_edit")) {
      const clean = (rows) => rows.filter((r) => r.brand.trim() && Number(r.qty) > 0)
        .map((r) => ({ brand: r.brand.trim(), capacity: r.capacity || null, qty: Number(r.qty) }));
      const eq = { panels: clean(equip.panels), inverters: clean(equip.inverters), batteries: clean(equip.batteries) };
      if (eq.panels.length || eq.inverters.length || eq.batteries.length) await api.post(`/field-items/${x.id}/equipment`, eq);
    }
  }, "Inspection submitted — awaiting admin approval");

  const saveEquipment = () => act(async () => {
    const clean = (rows) => rows.filter((r) => r.brand.trim() && Number(r.qty) > 0)
      .map((r) => ({ brand: r.brand.trim(), capacity: r.capacity || null, qty: Number(r.qty) }));
    await api.post(`/field-items/${x.id}/equipment`,
      { panels: clean(equip.panels), inverters: clean(equip.inverters), batteries: clean(equip.batteries) });
  }, "Equipment details saved");

  const saveAdminEdit = () => act(async () => {
    const clean = (rows) => rows.filter((r) => r.brand.trim() && Number(r.qty) > 0)
      .map((r) => ({ brand: r.brand.trim(), capacity: r.capacity || null, qty: Number(r.qty) }));
    await api.post(`/field-items/${x.id}/equipment`,
      { panels: clean(equip.panels), inverters: clean(equip.inverters), batteries: clean(equip.batteries) });
    const material_items = Object.values(values).filter((v) => Number(v.qty) > 0)
      .map((v) => ({ name: v.name, qty: Number(v.qty), spec: v.spec || null, unit: v.unit || null }));
    const structure = {
      rafters: rafters.filter((r) => Number(r.feet) > 0 && Number(r.qty) > 0).map((r) => ({ feet: Number(r.feet), qty: Number(r.qty) })),
      purlins: purlins.filter((r) => Number(r.feet) > 0 && Number(r.qty) > 0).map((r) => ({ feet: Number(r.feet), qty: Number(r.qty) })),
    };
    await api.post(`/field-items/${x.id}/admin-edit`, { surface_type: surface || null, structure, material_items });
    setEditing(false);
  }, "Changes saved");

  const photos = x.photos || [];
  const eqRows = (k) => x.equipment?.[k] || [];
  const MatTable = () => (
    ((x.material_items || []).length > 0 || x.equipment || x.structure) && (
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="bg-muted/60 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex justify-between">
          <span>Material List</span>
          {x.surface_type && <span>{x.surface_type === "flat" ? "Flat surface" : "Uneven surface"}</span>}
        </div>
        <div className="divide-y divide-border text-[12px]">
          {[["panels", "Panel"], ["inverters", "Inverter"], ["batteries", "Battery"]].map(([k, lbl]) =>
            eqRows(k).map((r, i) => (
              <div key={k + i} className="flex justify-between px-3 py-1.5 bg-amber-50/70 font-semibold">
                <span>{lbl}: {r.brand}{r.capacity ? ` · ${r.capacity}` : ""}</span><span className="font-mono">{r.qty} pcs</span>
              </div>
            )))}
          {(x.structure?.rafters || []).map((r, i) => (
            <div key={"rf" + i} className="flex justify-between px-3 py-1.5 bg-violet-50/60">
              <span className="font-medium">Rafter</span><span className="font-mono font-semibold">{r.feet} ft × {r.qty} pcs</span>
            </div>
          ))}
          {(x.structure?.purlins || []).map((r, i) => (
            <div key={"pl" + i} className="flex justify-between px-3 py-1.5 bg-violet-50/60">
              <span className="font-medium">Purlin</span><span className="font-mono font-semibold">{r.feet} ft × {r.qty} pcs</span>
            </div>
          ))}
          {(x.material_items || []).map((m, i) => (
            <div key={i} className="flex justify-between px-3 py-1.5">
              <span>{m.name}{m.spec ? <span className="text-muted-foreground"> · {m.spec}</span> : ""}</span>
              <span className="font-mono font-semibold">{m.qty} {m.unit || ""}</span>
            </div>
          ))}
        </div>
      </div>
    )
  );

  return (
    <div className="space-y-3">
      {/* Stage: PENDING — inspector fills */}
      {canFill && (
        <>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">How was the inspection done?</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="mode-site-visit" onClick={() => setMode("site_visit")}
                className={`h-12 rounded-xl border-2 flex items-center justify-center gap-2 text-sm font-semibold transition-all active:scale-95 ${mode === "site_visit" ? "border-violet-500 bg-violet-50 text-violet-700" : "border-border bg-card"}`}>
                <HouseLine size={18} /> Site Visit
              </button>
              <button type="button" data-testid="mode-phone-call" onClick={() => setMode("phone_call")}
                className={`h-12 rounded-xl border-2 flex items-center justify-center gap-2 text-sm font-semibold transition-all active:scale-95 ${mode === "phone_call" ? "border-violet-500 bg-violet-50 text-violet-700" : "border-border bg-card"}`}>
                <Phone size={18} /> Phone Call
              </button>
            </div>
          </div>
          <MaterialPicker catalog={catalog} values={values} setValues={setValues} />
          <StructureSection surface={surface} setSurface={setSurface} rafters={rafters} setRafters={setRafters} purlins={purlins} setPurlins={setPurlins} />
          {!isMgmt && (
            <p className="text-[11px] text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">Panel / Inverter / Battery brand, capacity and quantity are filled by Admin/Manager during approval.</p>
          )}
          {isMgmt && x.status === "pending" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Main Equipment (Admin/Manager only)</div>
              <EquipmentForm equip={equip} setEquip={setEquip} />
            </div>
          )}
          <div className="flex gap-2">
            <Input ref={noteRef} data-testid="insp-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note about the site" className="h-10 text-sm" />
            <VoiceTypingButton inputRef={noteRef} onText={setNote} />
          </div>
          <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-2.5 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1"><LockKey size={12} /> Private Note (Admin/Manager only)</div>
            <div className="flex gap-2">
              <Input ref={pvtRef} data-testid="insp-private-note" value={pvtNote} onChange={(e) => setPvtNote(e.target.value)} placeholder="Private detail" className="h-9 text-sm bg-white" />
              <VoiceTypingButton inputRef={pvtRef} onText={setPvtNote} />
              <VoiceRecorderButton disabled={busy} onRecorded={(blob) => act(async () => {
                const fd = new FormData(); fd.append("file", blob, "voice.webm");
                await api.post(`/field-items/${x.id}/private-voice`, fd);
              }, "Private voice note saved")} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PhotoUploader itemId={x.id} kind="site" onDone={() => act(async () => {})} label="Site Photo" />
            <Button data-testid="insp-submit-btn" className="h-11 px-5 rounded-full font-bold flex-1 min-w-[160px] active:scale-95"
              disabled={!mode || busy} onClick={submit}>
              <PaperPlaneRight size={16} className="mr-1.5" /> {x.status === "submitted" ? "Update & Resubmit" : "Submit Inspection"}
            </Button>
          </div>
        </>
      )}

      {/* Submitted summary for viewers — opens in a popup */}
      {!canFill && x.status !== "pending" && (x.material_items?.length || x.equipment || x.structure) && (
        <>
          <Button variant="outline" size="sm" data-testid="view-material-btn" onClick={() => setMatOpen(true)}
            className="h-9 rounded-full px-4 text-xs font-semibold active:scale-95">
            <Package size={14} className="mr-1.5" /> View Material Details
          </Button>
          <Dialog open={matOpen} onOpenChange={setMatOpen}>
            <DialogContent className="max-w-md" data-testid="material-view-dialog">
              <DialogHeader>
                <DialogTitle>Material Details</DialogTitle>
                <DialogDescription>{x.customer_name || x.text}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto"><MatTable /></div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Admin minor edit — change equipment / structure / materials anytime after approval */}
      {can("admin_edit") && !canFill && ["approved", "dispatched", "install_submitted", "completed"].includes(x.status) && (
        <div data-testid="admin-edit-section">
          {!editing ? (
            <Button variant="outline" size="sm" data-testid="admin-edit-btn"
              className="h-9 rounded-full px-4 text-xs font-semibold active:scale-95" onClick={() => setEditing(true)}>
              <PencilSimple size={14} className="mr-1" /> Edit details
            </Button>
          ) : (
            <div className="rounded-xl border border-violet-300 bg-violet-50/50 p-3 space-y-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-violet-800">Admin Edit — change as needed</div>
              <EquipmentForm equip={equip} setEquip={setEquip} />
              <MaterialPicker catalog={catalog} values={values} setValues={setValues} />
              <StructureSection surface={surface} setSurface={setSurface} rafters={rafters} setRafters={setRafters} purlins={purlins} setPurlins={setPurlins} />
              <div className="flex gap-2">
                <Button data-testid="admin-edit-save" className="h-10 rounded-full px-5 font-semibold flex-1 active:scale-95" disabled={busy} onClick={saveAdminEdit}>Save Changes</Button>
                <Button variant="outline" className="h-10 rounded-full px-5" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
      {!canFill && x.note && <p className="text-xs bg-muted/60 rounded-lg px-3 py-2"><b>Note:</b> {x.note}</p>}
      {isMgmt && x.private_note && (
        <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><LockKey size={11} className="inline mr-1" /><b>Private:</b> {x.private_note}</p>
      )}
      {isMgmt && x.private_voice && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <LockKey size={11} /> <b>Private voice:</b> <VoicePlayer url={`/field-items/${x.id}/private-voice`} />
        </div>
      )}
      {photos.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">{photos.map((p) => <FiPhoto key={p.id} itemId={x.id} photo={p} />)}</div>
      )}

      {/* Stage: SUBMITTED — admin review */}
      {x.status === "submitted" && can("approve") && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50/60 p-3 space-y-2.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Admin Review — fill Panel/Inverter/Battery and approve</div>
          {can("admin_edit") && <EquipmentForm equip={equip} setEquip={setEquip} />}
          {can("admin_edit") && (
            <Button data-testid="equipment-save-btn" size="sm" variant="outline" className="h-9 rounded-full px-4 text-xs font-semibold bg-white" disabled={busy}
              onClick={saveEquipment}>Save Equipment</Button>
          )}
          <Button data-testid="insp-approve-btn" className="w-full h-11 rounded-full font-bold bg-emerald-600 hover:bg-emerald-700 text-white active:scale-95" disabled={busy}
            onClick={() => act(() => api.post(`/field-items/${x.id}/approve`), "Approved — dispatch order sent to warehouse")}>
            <SealCheck size={17} className="mr-1.5" /> Approve & Send to Warehouse
          </Button>
        </div>
      )}
      {x.status === "submitted" && !can("approve") && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 animate-pulse" data-testid="awaiting-approval-note">Awaiting admin approval...</p>
      )}

      {/* Stage: APPROVED — warehouse dispatch */}
      {x.status === "approved" && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">Approved — the warehouse team will dispatch the material from the Dispatch portal.</p>
      )}

      {/* Dispatch history + pending (read-only; dispatch is managed in the warehouse portal) */}
      {(x.dispatches || []).length > 0 && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 px-3 py-2.5 text-xs space-y-2" data-testid="dispatch-details">
          <div className="font-bold text-indigo-800 flex items-center gap-1"><Truck size={13} /> Dispatch Records</div>
          {x.dispatches.map((ev, i) => (
            <div key={i} className="border-t border-indigo-200 pt-1.5 first:border-0 first:pt-0">
              <div className="flex justify-between font-semibold"><span>{ev.dispatch_date?.split("-").reverse().join("/")}</span>{ev.vehicle_no && <span className="font-mono">{ev.vehicle_no} · {ev.driver_name}</span>}</div>
              {(ev.items || []).map((it, j) => (
                <div key={j} className="flex justify-between text-muted-foreground"><span>{it.label}</span><span className="font-mono">{it.qty} {it.unit}</span></div>
              ))}
              <DeliveryRow x={x} ev={ev} can={can} act={act} busy={busy} />
            </div>
          ))}
          {(x.dispatch_pending_total || 0) > 0 && (
            <div className="border-t border-amber-300 pt-1.5 text-amber-700" data-testid="pending-material">
              <div className="font-bold flex items-center gap-1">Pending material</div>
              {(x.dispatch_lines || []).filter((l) => l.pending > 0).map((l) => (
                <div key={l.key} className="flex justify-between"><span>{l.label}</span><span className="font-mono">{l.pending} {l.unit}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      {x.dispatches_hidden && (
        <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2" data-testid="dispatch-sent-note">Material has been dispatched.</p>
      )}

      {/* Stage: DISPATCHED — tech completes install */}
      {x.status === "dispatched" && (isAssignee || isMgmt) && (
        <div className="rounded-xl border border-cyan-300 bg-cyan-50/60 p-3 space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-800">Fill details once installation is complete</div>
          <Input data-testid="install-note-input" value={installNote} onChange={(e) => setInstallNote(e.target.value)} placeholder="Installation detail / remarks" className="h-10 text-sm bg-white" />
          <div className="flex items-center gap-2 flex-wrap">
            <PhotoUploader itemId={x.id} kind="install" onDone={() => act(async () => {})} label="Install Photo" />
            <Button data-testid="install-complete-btn" className="h-11 px-5 rounded-full font-bold flex-1 min-w-[170px] bg-cyan-600 hover:bg-cyan-700 text-white active:scale-95" disabled={busy}
              onClick={() => act(() => api.post(`/field-items/${x.id}/install-complete`, { note: installNote }), "Installation submitted — awaiting admin approval")}>
              <CheckCircle size={16} className="mr-1.5" /> Installation Complete
            </Button>
          </div>
        </div>
      )}

      {/* Stage: INSTALL_SUBMITTED — admin final approval */}
      {x.status === "install_submitted" && (
        <>
          {x.install_note && <p className="text-xs bg-muted/60 rounded-lg px-3 py-2"><b>Install note:</b> {x.install_note}</p>}
          {can("approve") && can("insp_install_status") ? (
            <Button data-testid="final-approve-btn" className="w-full h-11 rounded-full font-bold bg-green-600 hover:bg-green-700 text-white active:scale-95" disabled={busy}
              onClick={() => act(() => api.post(`/field-items/${x.id}/approve`), "Installation approved — Job Complete")}>
              <SealCheck size={17} className="mr-1.5" /> Final Approve — Job Complete
            </Button>
          ) : (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 animate-pulse">Awaiting final admin approval...</p>
          )}
        </>
      )}

      {x.status === "completed" && (
        <p className="text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 flex items-center gap-2" data-testid="completed-note">
          <SealCheck size={18} weight="fill" /> Job Completed · approved by {x.approved_by || "Admin"}
        </p>
      )}
    </div>
  );
};
